import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { BodyTiny, Label } from '../styles/Typography'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  backgroundColor?: string
  placeholder?: string
  label?: string
  error?: string
  autoFocus?: boolean
  focusColor?: string
  borderColor?: string
  highlight?: boolean
}

export const TextInput = forwardRef<HTMLInputElement, InputProps>(
  ({
    backgroundColor = theme.color.white,
    label,
    error,
    placeholder,
    autoFocus,
    focusColor,
    borderColor,
    highlight,
    ...props
  }) => {
    return (
      <InputWrapper>
        {label && <StyledLabel>{label}</StyledLabel>}
        <StyledInput
          {...props}
          $borderColor={borderColor}
          $backgroundColor={backgroundColor}
          $focusColor={focusColor || theme.color.plum}
          $error={Boolean(error)}
          $highlight={highlight}
          placeholder={placeholder}
          autoFocus={autoFocus}
          min={props.type === 'number' ? 0 : undefined}
        />
        {error && <StyledErrorText>{error}</StyledErrorText>}
      </InputWrapper>
    )
  }
)

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledLabel = styled(Label)`
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.xsmall}px;
  && {
    text-transform: none;
  }
`

const StyledInput = styled.input<{
  $backgroundColor: string
  $error: boolean
  $focusColor: string
  $borderColor?: string
  $highlight?: boolean
}>`
  cursor: pointer;
  width: 100%;
  background: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: ${theme.spacing.xsmall}px;
  padding: ${theme.spacing.medium}px ${theme.spacing.xsmall}px;
  border: ${({ $error, $highlight, $borderColor }) => {
    if ($error) return `1px solid ${theme.color.red}`
    if ($highlight) return `2px solid ${theme.color.greenDark}`
    if ($borderColor) return `1px solid ${$borderColor}`
    return '1px solid transparent'
  }};
  font-family: ${theme.font.body};
  outline: 0;
  font-size: ${theme.fontSize.default}px;
  &:focus {
    border: 1px solid ${({ $borderColor, $focusColor }) => $borderColor || $focusColor};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const StyledErrorText = styled(BodyTiny)`
  margin-top: ${theme.spacing.tiny}px;
  color: ${theme.color.red};
  text-align: left;
`
